import { useEffect, useRef } from "react";

/**
 * Snap Creative Kit Web share button.
 *
 * Phase 27 (Feb 2026).
 *
 * Renders the official `snapchat-creative-kit-share` div. Snap's SDK
 * (loaded in public/index.html) scans for elements with this class and
 * transforms them into branded share buttons.
 *
 * Behavior:
 *  - On mobile w/ Snapchat installed → deep-links into Snapchat camera with
 *    our sticker pre-loaded and a swipe-up link back to `shareUrl`.
 *  - On desktop or no Snap installed → opens snap.com share preview.
 *
 * Props
 * -----
 *  - shareUrl: public URL Snap will scrape for OG/snap meta tags
 *  - label:    optional override (defaults to "Share to Snapchat")
 *  - testId:   data-testid passed through for E2E tests
 */
export function SnapShareButton({ shareUrl, label, testId = "snap-share-btn" }) {
  const ref = useRef(null);

  // Force Snap's SDK to re-scan after this element mounts (helpful for SPAs
  // where the SDK loaded BEFORE this component existed).
  useEffect(() => {
    if (!shareUrl) return;
    const t = setTimeout(() => {
      try {
        if (window.snapKitInit && typeof window.snapKitInit === "function") {
          window.snapKitInit();
        }
      } catch (e) { /* ignore */ }
    }, 50);
    return () => clearTimeout(t);
  }, [shareUrl]);

  if (!shareUrl) return null;

  return (
    <div
      ref={ref}
      className="snapchat-creative-kit-share"
      data-share-url={shareUrl}
      data-size="large"
      data-text={label || "Share to Snapchat"}
      data-testid={testId}
    />
  );
}
